// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// hex.ts — axial hex coordinates, and the integer key everything else is written in terms of.
//
// There is no board in brood, so there is no array to index by row and column. The cluster is a
// sparse set of occupied hexes that can grow in any direction, and every hot loop in the rules and
// the search wants a cheap, comparable, ARITHMETIC cell identifier. So a cell is ONE INTEGER: an
// axial (q,r) biased into a fixed 128x128 window and packed as `((q+64) << 7) | (r+64)`.
//
// The window is enormous relative to the game — the largest army is 11 a side, so the cluster can
// never span more than 22 hexes from the first tile, and a search can only push a destination one
// hex past the cluster per ply — which is why a fixed window is safe rather than a lurking
// overflow. What it buys is the line below: because the packing is linear in q and r, STEPPING TO
// A NEIGHBOUR IS ONE ADDITION. No decode, no re-encode, no allocation. That single fact is most of
// the difference between a bot that searches four plies on a phone and one that searches six.

/** Half-width of the coordinate window, in hexes. */
export const OFF = 64;

/** Pack an axial coordinate into a single non-negative integer (14 bits). */
export const key = (q: number, r: number): number => ((q + OFF) << 7) | (r + OFF);

/** The number of distinct keys — the size of every cell-indexed table. */
export const CELLS = 1 << 14;

/** The q of a packed key. */
export const keyQ = (k: number): number => (k >> 7) - OFF;
/** The r of a packed key. */
export const keyR = (k: number): number => (k & 127) - OFF;

/**
 * The six axial directions, in a fixed order. The INDEX is the direction id, and that id travels
 * on the wire inside a shunt ply, so this order is part of the protocol — never reorder it.
 */
export const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** The same six directions as KEY OFFSETS. `step(k, d)` is `k + DIR_OFF[d]`, which is why nothing
 *  in the hot path ever decodes a key. */
export const DIR_OFF: Int32Array = Int32Array.from(DIRS.map(([dq, dr]) => (dq << 7) + dr));

/** The neighbour of `k` in direction `d`. */
export const step = (k: number, d: number): number => k + DIR_OFF[d];

/** All six neighbours of `k`, in DIRS order. Allocates — never call it inside the search. */
export function neighbours(k: number): number[] {
  return [k + DIR_OFF[0], k + DIR_OFF[1], k + DIR_OFF[2], k + DIR_OFF[3], k + DIR_OFF[4], k + DIR_OFF[5]];
}

/**
 * The two cells flanking the edge between `k` and its neighbour in direction `d` — the cells
 * adjacent to BOTH. These are the gate posts: whether a tile can physically slide from one hex to
 * the next is decided entirely by what stands on them. On a hex grid the flanks of direction d are
 * simply directions d-1 and d+1.
 */
export const gateA = (k: number, d: number): number => k + DIR_OFF[(d + 5) % 6];
export const gateB = (k: number, d: number): number => k + DIR_OFF[(d + 1) % 6];
export const gate = (k: number, d: number): [number, number] => [gateA(k, d), gateB(k, d)];

/** Direction from `a` to adjacent `b`, or -1 if they are not neighbours. */
export function dirBetween(a: number, b: number): number {
  const off = b - a;
  for (let d = 0; d < 6; d++) if (DIR_OFF[d] === off) return d;
  return -1;
}

/** Are these two cells neighbours? */
export const adjacent = (a: number, b: number): boolean => dirBetween(a, b) >= 0;

/** Distance in hex steps between two cells. */
export function distance(a: number, b: number): number {
  const dq = keyQ(a) - keyQ(b);
  const dr = keyR(a) - keyR(b);
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

export const SQRT3 = Math.sqrt(3);

/**
 * Pixel centre of a hex for a given circumradius R, FLAT-TOP.
 *
 * Flat-top is not a stylistic pick, it is arithmetic. A flat-top hex is 2R wide with a column
 * pitch of only 1.5R, so columns interlock; a pointy-top hex is 1.732R wide with a 1.732R pitch
 * and does not. Horizontal room is the scarce resource on a 375px phone, and for the same tile
 * size flat-top fits about one more column into it — which, for a cluster that grows in every
 * direction, is the difference between fitting and panning.
 */
export function centre(k: number, radius: number): { x: number; y: number } {
  const q = keyQ(k);
  const r = keyR(k);
  return { x: radius * 1.5 * q, y: radius * SQRT3 * (r + q / 2) };
}

/** The same mapping at R = 1, which is what the frame fit works in. */
export const unitX = (k: number): number => 1.5 * keyQ(k);
export const unitY = (k: number): number => SQRT3 * (keyR(k) + keyQ(k) / 2);
